import { markNextSendAs } from "@/analytics/composerIntent";
import { useFlowSessionById } from "@/api/flow_sessions";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { useEventStream } from "@/providers/EventStreamProvider";
import { EventStreamEvent } from "@/types/events";
import { cn } from "@/utils";
import analytics from "@/utils/analytics";
import { FlowSession, FollowUpMessage } from "@cohesive/api-client";
import { defaultSuggestionIcon, Icon } from "@cohesive/ui-components";
import { AnimatePresence, motion } from "motion/react";
import React, {
  forwardRef,
  MouseEventHandler,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { z } from "zod";

interface FollowUpProps {
  flowSessionId: string;
  onMessageClick?: (message: FollowUpMessage) => void;
  followUpData?: FlowSession["follow_up_data"];
  showFollowUps?: boolean;
}

export interface FollowUpHandle {
  setFollowUpMessages: (message: FollowUpMessage[]) => void;
}

const DELAY = 0.05;

const followUpMessageSchema = z.object({
  title: z.string(),
  icon: z.object({
    type: z.string(),
    lucide: z.object({
      name: z.string(),
      color: z.string(),
    }),
  }),
  content: z.string(),
});

const followUpDataSchema = z.array(followUpMessageSchema);

const FollowUp = forwardRef<FollowUpHandle, FollowUpProps>(
  ({ flowSessionId, onMessageClick, followUpData, showFollowUps }, ref) => {
    const isDesktop = useBreakpoint("md");
    const [followUpMessages, setFollowUpMessages] = useState<FollowUpMessage[]>(
      [],
    );

    useEffect(() => {
      if (followUpData?.messages) {
        const result = followUpDataSchema.safeParse(followUpData.messages);

        if (result.success) {
          setFollowUpMessages(result.data as unknown as FollowUpMessage[]);
        } else {
          console.warn("Invalid follow up data structure:", result.error);
          setFollowUpMessages([]);
        }
      }
    }, [followUpData]);

    const { registerHandler } = useEventStream();
    const { data: flowSession } = useFlowSessionById(flowSessionId);

    useEffect(() => {
      const handleFlowSessionUpdate = (event: EventStreamEvent) => {
        if (event.type === "flow_session.updated") {
          const data = event.data;
          if (
            data.flow_session.id === flowSessionId &&
            data.flow_session.follow_up_data
          ) {
            const result = followUpDataSchema.safeParse(
              data.flow_session.follow_up_data,
            );
            if (result.success) {
              setFollowUpMessages(result.data as unknown as FollowUpMessage[]);
            } else {
              console.warn(
                "Invalid follow up data structure from websocket:",
                result.error,
              );
              setFollowUpMessages([]);
            }
          }
        }
      };

      return registerHandler(handleFlowSessionUpdate);
    }, [flowSessionId, registerHandler]);

    const handleMessageClick = (message: FollowUpMessage, position: number) => {
      // The chip fills the composer; the send it triggers carries no explicit
      // method, so mark it for useMessageActions to consume.
      markNextSendAs("follow_up");
      onMessageClick?.(message);

      if (flowSession) {
        analytics.trackFollowUpClicked({
          flowSessionId: flowSession.id,
          position,
        });
      }
    };

    // Design-system suggestion pill: rounded-full hairline chip with a small
    // leading icon; hover lifts the hairline to a 1px ring.
    const renderDesktopButton = (
      message: FollowUpMessage,
      index: number,
      onClick: MouseEventHandler<HTMLButtonElement>,
    ) => (
      <motion.div
        key={message.title}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 0, transition: { duration: 0.1 } }}
        transition={{ delay: index * DELAY }}
        className="m-0.5 max-w-full"
      >
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={onClick}
                className="inline-flex min-h-7 max-w-full items-center gap-2 rounded-full bg-card px-3 py-1 font-sans text-xs text-n-70 shadow-hairline outline-none transition-[box-shadow,color] duration-150 ease-out hover:text-n-90 hover:shadow-[0_0_0_1px_var(--n-30)] focus-visible:outline-2 focus-visible:outline-rust"
              >
                <span className="inline-flex shrink-0 items-center text-n-40">
                  <Icon
                    icon={message.icon || defaultSuggestionIcon}
                    size={14}
                  />
                </span>
                <span className="truncate">{message.title}</span>
              </button>
            </TooltipTrigger>
            <TooltipContent className="p-4 rounded-xl">
              <p className="max-w-[400px]">{message.content}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </motion.div>
    );

    useImperativeHandle(ref, () => ({
      setFollowUpMessages: (message: FollowUpMessage[]) => {
        setFollowUpMessages(message);
      },
    }));

    return (
      <div className={cn("relative flex justify-center w-full")}>
        <div
          className={cn(
            "absolute overflow-hidden w-full flex pb-0 bottom-0 transition-all bg-background duration-300",
            {
              "pb-2": showFollowUps,
              "overflow-x-auto": !isDesktop,
              "flex-wrap justify-center": isDesktop,
            },
          )}
        >
          <AnimatePresence>
            {followUpMessages.map((message, i) => {
              if (!showFollowUps) {
                return null;
              }

              return renderDesktopButton(message, i, () => {
                handleMessageClick(message, i);
              });
            })}
          </AnimatePresence>
        </div>
      </div>
    );
  },
);

export default React.memo(FollowUp);
